export type GooglePlaceReviewRow = {
  author_name: string;
  author_url?: string;
  profile_photo_url?: string;
  rating: number;
  relative_time_description: string;
  text: string;
  time: number;
};

export type GooglePlaceReviewsData = {
  place_id?: string;
  rating?: number;
  user_ratings_total?: number;
  reviews?: GooglePlaceReviewRow[];
  nextPageToken?: string;
};

function normalizePlaceDetailsPayload(data: unknown): GooglePlaceReviewsData | null {
  if (!data || typeof data !== "object") return null;
  const d = data as { success?: boolean; place?: Record<string, unknown> };
  if (!d.success || !d.place || typeof d.place !== "object") return null;
  const p = d.place;
  const reviewsRaw = p.reviews;
  const reviews = Array.isArray(reviewsRaw)
    ? reviewsRaw.map((review: Record<string, unknown>) => ({
        author_name: String(review.author_name || ""),
        author_url: review.author_url as string | undefined,
        profile_photo_url: review.profile_photo_url as string | undefined,
        rating: Number(review.rating || 0),
        relative_time_description: String(review.relative_time_description || ""),
        text: String(review.text || ""),
        time: Number(review.time || 0),
      }))
    : [];

  return {
    place_id: p.place_id as string | undefined,
    rating: typeof p.rating === "number" ? p.rating : undefined,
    user_ratings_total: typeof p.user_ratings_total === "number" ? p.user_ratings_total : undefined,
    reviews,
    nextPageToken:
      typeof (d as { nextPageToken?: unknown }).nextPageToken === "string"
        ? ((d as { nextPageToken?: string }).nextPageToken || undefined)
        : undefined,
  };
}

/** Server-side friendly: loads Google place reviews via backend (avoids browser → API fetch failures). */
export async function fetchStorePlaceDetailsForReviews(
  apiBase: string,
  covaLocationId: number,
  limit = 50,
  pageToken?: string
): Promise<GooglePlaceReviewsData | null> {
  if (!Number.isFinite(covaLocationId) || covaLocationId <= 0) return null;
  const base = apiBase.replace(/\/$/, "");
  try {
    const safeLimit = Math.min(Math.max(Number(limit || 50), 1), 50);
    const params = new URLSearchParams({ limit: String(safeLimit) });
    if (pageToken) params.set("pageToken", pageToken);
    const res = await fetch(`${base}/api/stores/${covaLocationId}/place-details?${params.toString()}`, {
      cache: "no-store",
    });
    if (!res.ok) return null;
    const json = await res.json();
    return normalizePlaceDetailsPayload(json);
  } catch {
    return null;
  }
}
